<template>
  <div>
    <div class="flex flex-column gap-3">
      <div class="flex justify-content-between align-items-center">
        <h3 class="m-0">Signed Documents</h3>
        <div class="flex gap-2">
          <a-button icon="export" type="primary" @click="$emit('export')">Export</a-button>
        </div>
      </div>

      <a-table
        size="small"
        :style="{ whiteSpace: 'pre' }"
        :columns="columns"
        :data-source="documents"
        :loading="loading"
        :pagination="pagination"
        :rowKey="record => record.id"
        :scroll="{ y: 380 }"
        :locale="{
          emptyText: searchValue ? getEmptyText() : 'No Documents'
        }"
        :rowClassName="getRowClassName"
        @change="handleTableChange"
        :customRow="
          record => ({
            on: {
              click: () => handleDocumentView(record)
            }
          })
        "
      >
        <span slot="date" slot-scope="text, record">
          {{ formatDateString(record.signature.signed_date) }}
        </span>

        <span slot="actions" slot-scope="text, record">
          <span :style="{ display: 'none' }">
            <export-button
              :ref="`exportRef_${record.id}`"
              :only-icon="true"
              :export-payload="exportDocumentPayload(record.signed_version_id)"
              @updateUrl="url => afterDownloadDocument(url, record.signed_version_id)"
          /></span>
          <button-more :record="record" :menu-items="menuItems" />
        </span>
      </a-table>
    </div>
  </div>
</template>

<script>
import { Button, notification, Table } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import ExportButton from '@/components/export-button.vue'
import { h } from 'vue'
import tableMixin from '@/mixins/tableMixin'
import BulkArchiveMixin from '../mixins/BulkArchiveMixin'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: documentActions } = createNamespacedHelpers('document')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-table': Table,
    'export-button': ExportButton,
    'a-button': Button
  },
  props: {
    searchValue: {
      type: String,
      default: undefined
    }
  },
  mixins: [tableMixin, BulkArchiveMixin],
  data() {
    return {
      pagination: {
        showSizeChanger: true,
        pageSizeOptions: ['5', '10', '20', '50'],
        pageSize: 50,
        total: 0,
        current: 1
      },

      documents: [],
      loadingButtons: {},

      columns: [
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          width: 300,
          sorter: (a, b) => (a.name == null ? -1 : b.name == null ? 1 : a.name.localeCompare(b.name))
        },
        {
          title: 'Signed Date',
          dataIndex: 'date',
          key: 'date',
          width: 150,
          scopedSlots: { customRender: 'date' },
          sorter: (a, b) => {
            const dateA = a.signature.signed_date ? new Date(a.signature.signed_date) : null
            const dateB = b.signature.signed_date ? new Date(b.signature.signed_date) : null

            if (!dateA && !dateB) return 0
            if (!dateA) return 1
            if (!dateB) return -1

            return dateA - dateB
          }
        },
        {
          title: 'Actions',
          dataIndex: 'actions',
          key: 'actions',
          width: 120,
          scopedSlots: { customRender: 'actions' }
        }
      ],

      loading: false
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate,
      signedDocuments: state => state.signedDocuments
    }),

    menuItems() {
      return [
        {
          key: 'open',
          icon: 'fluent:open-20-regular',
          label: 'Open',
          visible: true,
          handler: record => {
            this.handleDocumentView(record)
          }
        },
        {
          key: 'archive',
          icon: 'carbon:archive',
          label: 'Delete',
          visible: true,
          handler: record => {
            this.deleteDocument(record.signed_version_id)
          }
        },
        {
          key: 'bulk-archive-signed',
          icon: 'carbon:archive',
          label: 'Archive Signed Documents',
          visible: true,
          handler: record => {
            this.openBulkArchiveConfirm({
              type: 'document',
              object_id: record.id,
              name: record.name,
              onDone: () => this.fetchDocuments()
            })
          }
        },
        {
          key: 'export',
          icon: 'mdi:export',
          label: 'Export',
          visible: true,
          children: [
            {
              key: 'send-export-document',
              icon: 'carbon:email',
              label: 'Send to Email',
              visible: true,
              handler: record => {
                const refName = `exportRef_${record.id}`

                if (this.$refs[refName]) {
                  this.$refs[refName].openSendModal()
                }
              }
            },
            {
              key: 'download-export-document',
              icon: 'carbon:document-download',
              label: 'Download',
              visible: true,
              handler: record => {
                if (record.export_file) {
                  window.open(record.export_file.url, '_blank')
                } else {
                  const refName = `exportRef_${record.id}`

                  this.$refs[refName].download()
                }
              }
            }
          ]
        }
      ]
    }
  },
  watch: {
    signedDocuments: {
      handler(newVal) {
        this.pagination.total = newVal?.total_count ?? 0
        this.documents = newVal.items ?? []
      },
      immediate: true,
      deep: true
    },

    searchValue: {
      handler() {
        this.onSearch()
      }
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...profileActions(['getSignedDocuments', 'setSignedDocuments']),
    ...documentActions(['exportDocument', 'archiveAssociateSignedDocument']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    afterDownloadDocument(url, id) {
      const updatedDocuments = {
        ...this.signedDocuments,
        items: this.signedDocuments.items.map(item => {
          if (item.signed_version_id === id) {
            return { ...item, export_file: { url } }
          } else {
            return item
          }
        })
      }

      this.setSignedDocuments(updatedDocuments)
    },

    getEmptyText() {
      return h('span', [
        'Nothing matched your search. Please take a look at ',
        h(
          'span',
          {
            on: {
              click: () => {
                this.$emit('click', 'attached')
              }
            },
            style: { color: '#2563eb', cursor: 'pointer' }
          },
          'Attached Documents'
        ),
        ' — maybe you’ll find it there!'
      ])
    },

    onSearch() {
      this.pagination.current = 1

      this.fetchDocuments()
    },

    handleDocumentView(document) {
      this.openModal({
        type: 'documentView',
        data: {
          visible: true,
          associate_id: this.associate.id,
          version: document.current_version,
          document_id: document.id
        }
      })
    },
    exportDocumentPayload(signed_version_id) {
      return {
        type: 'project_document_version',
        object_ids: [signed_version_id]
      }
    },

    formatDateString(dateString) {
      return moment(dateString).format('MM/DD/YYYY')
    },

    handleTableChange(pagination) {
      const isPaginationChanged =
        pagination.current !== this.pagination.current || pagination.pageSize !== this.pagination.pageSize

      if (!isPaginationChanged) {
        return
      }

      this.pagination = { ...pagination }

      this.fetchDocuments()
    },

    async fetchDocuments() {
      this.loading = true

      const payload = {
        associate_id: this.associate.id,
        page: this.pagination.current,
        size: this.pagination.pageSize,
        filter: {
          name: this.searchValue === '' ? undefined : this.searchValue
        }
      }

      try {
        await this.getSignedDocuments(payload)
      } catch (error) {
        this.showNotification('error', 'Error', error || 'An error occurred')
      } finally {
        this.loading = false
      }
    },

    async deleteDocument(id) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Are you sure?',
          description:
            'After deleting, the document will disappear from the profile and you will only be able to restore it through ePeople support. The person will need to re-upload it. Are you sure you want to delete the document?',
          onConfirm: async () => {
            await this.archiveAssociateSignedDocument({
              id
            })

            this.showNotification('success', 'Success', 'Successfully deleted the document.')

            this.fetchDocuments()
          }
        }
      })
    }
  },
  async mounted() {
    await this.fetchDocuments()
  }
}
</script>

<style scoped>
::v-deep .ant-table-row-cell-break-word {
  text-wrap: initial;
}
</style>
